<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { sumBy } from 'lodash-es';
import Download from '../PaymentsDownload';
import Overlay from '../../TwOverlay';

export default {
  name: 'ClientPaymentTable',
  components: {
    Download,
    Overlay,
  },
  computed: {
    ...mapState({
      rows: state => state.clientPayments.tableData.rows,
      loading: state => state.clientPayments.tableData.loading,
      loaded: state => state.clientPayments.tableData.loaded,
      error: state => state.clientPayments.tableData.error,
      downloadListLoading: state => state.clientPayments.download.list.loading,
      downloadListError: state => state.clientPayments.download.list.error,
      downloadSpecLoading: state => state.clientPayments.download.specifications.loading,
      downloadSpecError: state => state.clientPayments.download.specifications.error,
      downloadSpecName: state => state.clientPayments.download.specifications.name,
    }),
    ...mapGetters({
      headers: 'clientPayments/getTableHeaders',
      downloadInfo: 'clientPayments/getDownloadSearchInfo',
    }),
    totalAmounts() {
      return {
        liveInEuro: sumBy(this.rows, 'liveInEuro'),
        cancelledInEuro: sumBy(this.rows, 'cancelledInEuro'),
      };
    },
    downloadDisabled() {
      return this.loading ||
        this.error ||
        !this.rows ||
        !this.rows.length ||
        this.downloadListLoading ||
        this.downloadSpecLoading;
    },
  },
  methods: {
    ...mapActions({
      downloadList: 'clientPayments/downloadPayments',
      downloadSpecifications: 'clientPayments/downloadPaymentSpecifications',
    }),
  },
  filters: {
    formatNumber: number => parseFloat(number).toFixed(2)
      .replace(/\./, ',')
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
  },
  data() {
    return {
      rowsPerPageItems: [5, 10, 25, 50, 100, { text: this.$t('inputs.all'), value: -1 }],
      pagination: {
        rowsPerPage: 25,
      },
    };
  },
};
</script>

<template>
  <v-flex class="client-payment-table xs12 mt-4">
    <h1 class="my-1 text-xs-center">{{ $t('clientPayments.payments') }}</h1>
    <download
      @download="downloadList"
      :disabled="downloadDisabled"
      :loading="downloadListLoading"
      :error="downloadListError"
      :error-message="$t('payments.actions.downloadError')"
      :info="downloadInfo"
    />
    <v-divider/>
    <v-flex class="client-payment-table__table xs12">
      <overlay :visible="downloadSpecLoading"/>
      <v-data-table
        :headers="headers"
        :items="rows"
        :pagination.sync="pagination"
        :rows-per-page-items="rowsPerPageItems"
        :loading="loading"
        disable-initial-sort
      >
        <template slot="items" slot-scope="props">
          <tr
            class="client-payment-table__table-row"
            @click="downloadSpecifications(props.item.domain)"
          >
            <td class="text-xs-left">{{ props.item.domain.name }}</td>
            <td class="text-xs-right">{{ props.item.live | formatNumber }}</td>
            <td class="text-xs-right">{{ props.item.cancelled | formatNumber }}</td>
            <td class="text-xs-center">{{ props.item.currency }}</td>
            <td class="text-xs-right">{{ props.item.liveInEuro | formatNumber }}</td>
            <td class="text-xs-right">{{ props.item.cancelledInEuro | formatNumber }}</td>
          </tr>
        </template>
        <template slot="footer">
          <tr v-if="rows.length" class="client-payment-table__footer">
            <td class="grey--text caption" colspan="3">
              {{ $t('payments.actions.downloadDomain') }}
            </td>
            <td class="text-xs-center"><b>{{ $t('clientPayments.table.total') }}:</b></td>
            <td class="text-xs-right">{{ totalAmounts.liveInEuro | formatNumber }} €</td>
            <td class="text-xs-right">{{ totalAmounts.cancelledInEuro | formatNumber }} €</td>
          </tr>
        </template>
        <template slot="no-data">
          <div v-if="error">
            <v-alert :value="true" icon="warning">
              {{ $t('payments.actions.error') }}.
              <span class="client-payment-table__text-link" @click="$emit('refresh')">
              {{ $t('inputs.misc.tryAgain') }}
            </span>
            </v-alert>
          </div>
          <div v-else-if="loading">
            <!--Show nothing when loading-->
          </div>
          <div class="text-xs-center" v-else-if="!loaded">
            <span>{{ $t('payments.actions.performSearch') }}</span>
          </div>
          <div class="text-xs-center" v-else>
            <span>{{ $t('inputs.misc.noContent') }}</span>
          </div>
        </template>
      </v-data-table>
    </v-flex>
    <v-snackbar
      :value="downloadSpecName"
      :timeout="0"
      bottom
    >
      <v-progress-circular
        color="primary"
        size="25"
        width="3"
        indeterminate
      />
      <span class="ml-3">
        {{ $t('payments.actions.downloadSpecLoading', { name: downloadSpecName }) }}
      </span>
    </v-snackbar>
    <v-snackbar
      :value="downloadSpecError"
      bottom
    >
      {{ $t('payments.actions.downloadSpecError') }}
    </v-snackbar>
  </v-flex>
</template>

<style lang="scss">
  .client-payment-table {
    position: relative;

    &__table {
      position: relative;
    }

    &__footer {
      & > td:nth-child(n+6) {
        border-left: 1px solid black;
      }
    }

    &__text-underline {
      text-decoration: underline;
    }

    &__text-link {
      text-decoration: underline;
      cursor: pointer;
    }

    &__table-row {
      cursor: pointer;
    }

    &__table-row > td:not(:first-child) {
      border-left: 1px solid black;
    }

    &__table-row:nth-child(even) {
      background-color: #f5f5f5;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/payments/client-payments/ClientPaymentsTable.vue